import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AxiosErrorHandler,
  Form,
  FormActions,
  InfinityScrollSelect,
  Modal,
  RichTextRenderer,
  Skeleton,
  Space,
  Text,
} from 'ui';
import { useChangelogVersion, useVersionCompare } from './hooks';
import { useReleaseNotesQuery } from './queries';

type ChangelogFormValues = {
  version?: VersionOption;
};

type VersionOption = {
  changelog: {
    htmlContent: string;
    version: string;
  };
  label: string;
  value: string;
};

const ChangelogModal = ({ isOpened, onClose }: { isOpened: boolean; onClose: () => void }) => {
  const { t } = useTranslation();
  const {
    data: releaseNotes,
    error: queryError,
    fetchNextPage: fetchNextPageOfReleaseNotes,
    hasNextPage: hasMoreReleaseNotes,
    isFetching: isFetchingReleaseNotes,
    isLoading: isLoadingReleaseNotes,
  } = useReleaseNotesQuery({ limit: 10 });

  const { compareVersions } = useVersionCompare();

  const { updateLatestChangelogVersion } = useChangelogVersion();

  const allFetchedReleaseNotes = useMemo(
    () =>
      releaseNotes?.pages.flatMap((page) =>
        page.data.map((option) => ({
          changelog: {
            htmlContent: option.text,
            version: option.versionValue,
          },
          label: compareVersions({
            backendVersion: option.versionValue,
            packageVersion: String(import.meta.env.VITE_APP_VERSION),
          })
            ? `${option.versionValue} - Current version`
            : option.versionValue,
          value: option.id,
        })),
      ),
    [compareVersions, releaseNotes?.pages],
  );

  const initialValues: ChangelogFormValues = {
    version: allFetchedReleaseNotes?.[0],
  };

  const handleCancel = () => {
    updateLatestChangelogVersion();
    onClose();
  };

  return (
    <Modal isOpened={isOpened} maskClosable={false} onCancel={handleCancel} title="Release notes" width="medium">
      <Modal.HugContent>
        <AxiosErrorHandler error={queryError}>
          {isLoadingReleaseNotes || !allFetchedReleaseNotes ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <Form<ChangelogFormValues> initialValues={initialValues}>
              <Space orientation="vertical" size="middle">
                <Text>
                  Here&apos;s what&apos;s new! The latest release includes new features, improvements, and bug fixes to
                  make your experience better than ever
                </Text>
                <Form.Item<ChangelogFormValues> label="Select version" name="version" rules={[{ required: true }]}>
                  <InfinityScrollSelect<VersionOption>
                    handleRequestMoreData={fetchNextPageOfReleaseNotes}
                    hasMoreData={hasMoreReleaseNotes}
                    loading={isFetchingReleaseNotes}
                    options={allFetchedReleaseNotes}
                    placeholder={t('Select version')}
                  />
                </Form.Item>
                <Form.Item<ChangelogFormValues> dependencies={['version']} noStyle>
                  {({ getFieldsValue }) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                    const { version } = getFieldsValue(true) as ChangelogFormValues;

                    const htmlContent = version?.changelog.htmlContent;

                    return htmlContent ? <RichTextRenderer data={htmlContent} /> : null;
                  }}
                </Form.Item>
              </Space>
              <FormActions submit={{ children: 'Got it', onClick: handleCancel }} />
            </Form>
          )}
        </AxiosErrorHandler>
      </Modal.HugContent>
    </Modal>
  );
};

export default ChangelogModal;
